import { Text } from 'pixi.js';
import { Scene } from '../engine/scene';
import { getContext } from '../engine/context';
import { DifficultyCard } from '../ui/DifficultyCard';
import { GAME_HEIGHT, GAME_WIDTH } from '../config/tuning';
import type { Difficulty } from '../config/difficulty';
import { playUIClick } from '../engine/audio';

/**
 * DifficultyScene — four opponent cards in a single horizontal row.
 * Positions use proportions of the design space (1920×1080) so the layout
 * looks balanced on any screen the letterbox renderer maps to.
 */
export class DifficultyScene extends Scene {
  async onEnter(): Promise<void> {
    const ctx = getContext();
    const cx = GAME_WIDTH / 2;

    // Title
    const title = new Text({
      text: 'CHOOSE YOUR OPPONENT',
      style: {
        fontFamily: 'Impact, Arial Black, sans-serif',
        fontSize: 48,
        fill: 0xffd23f,
        stroke: { color: 0x1a0d22, width: 5 },
        letterSpacing: 4,
      },
    });
    title.anchor.set(0.5);
    title.position.set(cx, GAME_HEIGHT * 0.13);
    this.addChild(title);

    // Subtitle
    const sub = new Text({
      text: 'Pick wisely. Each one plays different.',
      style: {
        fontFamily: 'Inter, sans-serif',
        fontSize: 16,
        fill: 0x5fdbcf,
        fontStyle: 'italic',
      },
    });
    sub.anchor.set(0.5);
    sub.position.set(cx, GAME_HEIGHT * 0.19);
    this.addChild(sub);

    const cards: Array<{ key: string; diff: Difficulty }> = [
      { key: 'ui_card_easy', diff: 'easy' },
      { key: 'ui_card_medium', diff: 'medium' },
      { key: 'ui_card_hard', diff: 'hard' },
      { key: 'ui_card_narco', diff: 'narco' },
    ];

    // Cards: 240×320 native at scale 1.15 → 276×368 each. The DifficultyCard
    // inner visual adds another ~1.05×, so effective ~290×386. At 330px
    // center-to-center the 4 cards span 990px, centered on the 1600-wide
    // canvas with ~305px margins.
    const cardScale = 1.15;
    const spacing = 330;
    const startX = cx - (spacing * (cards.length - 1)) / 2;
    const cardY = GAME_HEIGHT * 0.56;

    cards.forEach((c, i) => {
      const card = new DifficultyCard({
        x: startX + i * spacing,
        y: cardY,
        textureKey: c.key,
        difficulty: c.diff,
        onClick: (d) => ctx.sceneManager.start('GameScene', { difficulty: d }),
        audio: ctx.audio,
      });
      card.scale.set(cardScale);
      this.addChild(card);
    });

    // Back link
    const back = new Text({
      text: '‹ Back to Menu',
      style: {
        fontFamily: 'Impact, sans-serif',
        fontSize: 26,
        fill: 0x5fdbcf,
      },
    });
    back.anchor.set(0.5);
    back.position.set(cx, GAME_HEIGHT - 60);
    back.eventMode = 'static';
    back.cursor = 'pointer';
    back.on('pointerover', () => { back.style.fill = 0xffd23f; });
    back.on('pointerout', () => { back.style.fill = 0x5fdbcf; });
    back.on('pointerdown', () => {
      playUIClick(ctx.audio);
      ctx.sceneManager.start('MenuScene');
    });
    this.addChild(back);
  }
}
